/**
 * web/src/hooks/queries/useOpsEmailThread.ts
 * Live welcome→ops Gmail thread for the ops_next_steps gate viewer. Read from
 * the reviewer's selected send-from mailbox so the conversation matches the
 * account they'll send the ops email as. The server is the source of truth and
 * never errors for the unavailable cases (it returns `available: false`), so
 * this is a plain enabled query with no special retry handling.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchOpsEmailThread } from '../../api/workflows';
import { qk } from '../../lib/queryKeys';

/**
 * Fetches GET /api/workflows/:id/ops-email-thread for the gate thread viewer.
 *
 * @param workflowId workflow id; query disabled while undefined or when `enabled` is false
 * @param sendFromConnectionId the gate's selected send-from GmailConnection id
 * @param enabled gate it on ops_next_steps only (avoid a Gmail read on other gates)
 * @returns React Query result for OpsEmailThread
 * @sideEffects HTTP GET (triggers a live Gmail read server-side)
 * @idempotent yes
 */
export function useOpsEmailThread(
  workflowId: string | undefined,
  sendFromConnectionId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: qk.workflow.opsThread(workflowId, sendFromConnectionId),
    queryFn: () => fetchOpsEmailThread(workflowId!, sendFromConnectionId),
    enabled: enabled && workflowId != null,
    // A Gmail thread doesn't change second-to-second; avoid re-reading the
    // mailbox on every window focus while the reviewer composes.
    staleTime: 60_000,
  });
}
